document.addEventListener('DOMContentLoaded', function () {
  class MyElement extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
    }

    connectedCallback () {
      this.render();
    }

    render () {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            padding: 10px;
            background-color: #f0f0f0;
            border: 1px solid #ddd;
          }
        </style>
        <div>
          <h2>My Custom Element</h2>
        </div>
      `;
    }
  }

  if (!window.customElements.get('my-element')) {
    customElements.define('my-element', MyElement);
  }

  // 创建 IntersectionObserver 实例
  window.YGImageLoader = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        const newImg = new Image();
        newImg.onload = () => {
          img.src = newImg.src;
          img.removeAttribute('data-src');  // 如果只需要加载一次
          img.removeAttribute('srcset');
          observer.unobserve(img);          // 停止观察该元素
        }
        newImg.src = img.getAttribute('data-src');
      }
    });
  }, {
    rootMargin: "50px", // 预加载距离视口 50px 的图片
  });

  class YGImage extends HTMLElement {
    constructor() {
      super()
      this.init()
    }

    get images () {
      return this.querySelectorAll('img[data-src]')
    }

    init () {
      // 这里放置初始化逻辑
      this.loaderImg()
    }

    loaderImg () {
      // 这里放置自定义逻辑
      this.images.forEach((img) => {
        window.YGImageLoader.observe(img)
      })
    }
  }

  if (!window.customElements.get('yg-image')) {
    window.customElements.define('yg-image', YGImage)
  }

  const onYouTubePromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => resolve();
  });

  class YGDeferredMedia extends HTMLElement {
    constructor() {
      super();
    }

    static get observedAttributes () {
      return ['playing'];
    }

    connectedCallback () {
      this.bindPosterButton();

      this.addEventListener('click', (e) => {
        const video = this.querySelector('video')
        if (
          video &&
          e.target === video &&
          !video.hasAttribute('controls')
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (this.playing) {
            this.pause()
          } else {
            this.play()
          }
        }
      });

      if (this.autoplay) {
        this.inViewObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init();
            }
          });
        });
        this.inViewObserver.observe(this);
      }
    }

    get posterElement () {
      return this.querySelector('[id^="YG-DeferredPoster-"]');
    }

    get autoplay () {
      return this.hasAttribute('autoplay');
    }

    get playing () {
      return this.hasAttribute('playing');
    }

    get player () {
      return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target;
            this.playerHandler(target, prop);
          };
        }
      });
    }

    bindPosterButton () {
      const poster = this.posterElement;
      if (poster && !poster.hasListener) {
        poster.addEventListener('click', this.onPosterClick.bind(this));
        poster.hasListener = true;
      }
    }

    async playerTarget () {
      const template = this.querySelector('template');
      if (!template) return Promise.resolve(null);

      if (!this.mediaElement) {
        const fragment = template.content.cloneNode(true);
        this.appendChild(fragment);
        this.mediaElement = this.querySelector('video, iframe');
      }

      this.bindPosterButton();
      return Promise.resolve(this.mediaElement);
    }

    playerHandler (target, prop) {
      if (!target) return;

      if (prop === 'play' && typeof target.play === 'function') {
        target.play();
        this.setAttribute('playing', '');
        this.removeAttribute('paused');
      }

      if (prop === 'pause' && typeof target.pause === 'function') {
        target.pause();
        this.removeAttribute('playing');
        this.setAttribute('paused', '');
      }
    }

    onPosterClick (event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.playing) {
        this.pause();
      } else {
        this.play();
      }
    }

    init () {
      if (!this.paused) {
        this.play();
      }
      return () => this.pause();
    }

    play () {
      if (!this.playing) {
        this.player.play();
      }
    }

    pause () {
      if (this.playing) {
        this.player.pause();
      }
    }

    attributeChangedCallback (name, oldValue, newValue) {
      if (name === 'playing') {
        if (oldValue === null && newValue === '') {
          this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
          this.removeAttribute('paused');
        }
        if (newValue === null) {
          this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
          this.setAttribute('paused', '');
        }
      }
    }
  }

  class YGMedia extends YGDeferredMedia {
    constructor() {
      super();
    }

    connectedCallback () {
      super.connectedCallback(); // 保留父类逻辑

      if (!this.querySelector('img') && !this.hasAttribute('autoplay')) {
        this.inViewObserverNoAutoplay = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadMediaOnly();
              this.inViewObserverNoAutoplay.disconnect();
            }
          });
        });
        this.inViewObserverNoAutoplay.observe(this);
      }
    }

    async loadMediaOnly () {
      if (!this.mediaElement) {
        const template = this.querySelector('template');
        if (template) {
          const fragment = template.content.cloneNode(true);
          this.appendChild(fragment);
          this.mediaElement = this.querySelector('video, iframe');
          this.setAttribute('loaded', '');
          this.closest('.media')?.classList.remove('loading');
          this.bindPosterButton();
        }
      }
    }

    async playerTarget () {
      if (this.hasAttribute('host')) {
        this.setAttribute('loaded', '');
        this.closest('.media')?.classList.remove('loading');

        return new Promise(async (resolve) => {
          const templateElement = this.querySelector('template');
          if (templateElement) {
            templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
          }
          this.bindPosterButton();

          const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('(max-width: 1023px)').matches;
          const host = this.getAttribute('host');

          if (host === 'youtube') {
            if (!window.YT || !window.YT.Player) {
              await new Promise(res => {
                const script = document.createElement('script');
                script.src = 'https://www.youtube.com/iframe_api';
                script.onload = res;
                document.head.appendChild(script);
              });
              await new Promise(res => {
                window.onYouTubeIframeAPIReady = res;
              });
            }

            const player = new YT.Player(this.querySelector('iframe'), {
              events: {
                onReady: () => {
                  if (muteVideo) player.mute();
                  resolve(player);
                },
                onStateChange: (event) => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setAttribute('playing', '');
                    this.removeAttribute('paused');
                  }
                  if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
                    this.removeAttribute('playing');
                    this.setAttribute('paused', '');
                  }
                }
              }
            });
          }

          if (host === 'vimeo') {
            if (!window.Vimeo || !window.Vimeo.Player) {
              await new Promise(res => {
                const script = document.createElement('script');
                script.src = 'https://player.vimeo.com/api/player.js';
                script.onload = res;
                document.head.appendChild(script);
              });
            }

            const player = new Vimeo.Player(this.querySelector('iframe'));
            if (muteVideo) await player.setMuted(true);

            player.on('play', () => {
              this.setAttribute('playing', '');
              this.removeAttribute('paused');
            });
            player.on('pause', () => {
              this.removeAttribute('playing');
              this.setAttribute('paused', '');
            });
            player.on('ended', () => {
              this.removeAttribute('playing');
              this.setAttribute('paused', '');
            });

            resolve(player);
          }

          if (host === 'pimmedia') {
            // 假设 pimmedia 的 iframe 已经在页面上
            const iframe = this.querySelector('iframe');
            console.log("第三季我")
            if (!iframe) {
              console.warn('pimmedia iframe not found');
              return;
            }

            // 假设 mute 需要 postMessage 或类似接口，这里根据你具体接口改写
            if (muteVideo) {
              // 这里看你的 pimmedia 播放器怎么静音，比如 postMessage
              iframe.contentWindow.postMessage({ action: 'mute' }, '*');
            }

            // 监听 pimmedia 的消息事件（例如播放、暂停、结束）
            window.addEventListener('message', (event) => {
              // 确认是 pimmedia 的消息，可根据 event.origin 做验证
              const data = event.data;

              if (typeof data !== 'object' || !data.event) return;

              if (data.event === 'play') {
                this.setAttribute('playing', '');
                this.removeAttribute('paused');
              }
              if (data.event === 'pause' || data.event === 'ended') {
                this.removeAttribute('playing');
                this.setAttribute('paused', '');
              }
            });

            // 返回一个模拟的 player 对象给 resolve
            resolve({
              play () {
                iframe.contentWindow.postMessage({ action: 'play' }, '*');
              },
              pause () {
                iframe.contentWindow.postMessage({ action: 'pause' }, '*');
              },
              mute () {
                iframe.contentWindow.postMessage({ action: 'mute' }, '*');
              }
            });
          }
        });
      } else {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        this.setAttribute('loaded', '');
        this.closest('.media')?.classList.remove('loading');

        const player = this.querySelector('video');
        player.addEventListener('play', () => {
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
          this.removeAttribute('suspended');
        });
        player.addEventListener('pause', () => {
          if (!player.seeking && player.paused) {
            this.removeAttribute('playing');
            this.setAttribute('paused', '');
          }
        });

        this.bindPosterButton();
        return player;
      }
    }

    playerHandler (target, prop) {
      const host = this.getAttribute('host');

      if (host === 'youtube') {
        if (prop === 'play') {
          target.playVideo();
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
        } else {
          target.pauseVideo();
          this.removeAttribute('playing');
          this.setAttribute('paused', '');
        }
      } else if (host === 'vimeo') {
        if (prop === 'play') {
          target.play();
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
        } else {
          target.pause();
          this.removeAttribute('playing');
          this.setAttribute('paused', '');
        }
      } else {
        if (prop === 'play') {
          target.play().catch((error) => {
            if (error.name === 'NotAllowedError') {
              this.setAttribute('suspended', '');
              target.controls = true;
              const replacementImageSrc = target.previousElementSibling?.currentSrc;
              if (replacementImageSrc) {
                target.poster = replacementImageSrc;
              }
            }
          });
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
        } else {
          target.pause();
          this.removeAttribute('playing');
          this.setAttribute('paused', '');
        }
      }
    }
  }

  if (!customElements.get('yg-video')) {
    customElements.define('yg-video', YGMedia);
  }

  class YGSlide extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback () {
      this.initSwiper()
    }

    initSwiper () {
      new Swiper(this, {
        pagination: {
          el: '.yg-slide-pagination',
          // 数字指示器
          // type: 'fraction',
          clickable: true,
        },
        navigation: {
          nextEl: this.querySelector('.yg-slide-next'),
          prevEl: this.querySelector('.yg-slide-prev'),
        },
        breakpoints: {
          220: {
            slidesPerView: 1.2,
            spaceBetween: 16,
          },
          700: {
            slidesPerView: 2,
            spaceBetween: 12,
          },
          1150: {
            slidesPerView: 3,
            spaceBetween: 12,
          },
        }
      })
    }
  }

  if (!window.customElements.get('yg-slide')) {
    window.customElements.define('yg-slide', YGSlide)
  }

  class ScrollingImages extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('mouseover', this._pauseScrolling.bind(this));
      this.addEventListener('mouseout', this._resumeScrolling.bind(this));

      this.resizeObserver = new ResizeObserver((entries) => {
        this._calculateDuration(entries);
        this._duplicateIfNeeded();
      });
    }

    connectedCallback () {
      this.resizeObserver.observe(this);
    }

    disconnectedCallback () {
      this.resizeObserver.disconnect();
    }

    _calculateDuration (entries) {
      const scrollingSpeed = parseInt(this.getAttribute('scrolling-speed') || 20),
        contentWidth = entries[0].contentRect.width,
        slowFactor = 1 + (Math.min(15000, contentWidth) - 375) / (1200 - 375);

      const contentDiv = this.querySelector('img');
      if (contentDiv) {
        this.style.setProperty(
          '--marquee-animation-duration',
          `${((scrollingSpeed * slowFactor * contentDiv.clientWidth) / contentWidth).toFixed(3)}s`
        );
      }
    }

    _pauseScrolling () {
      this.parentNode.querySelectorAll('scrolling-image').forEach((item) => {
        item.style.animationPlayState = 'paused';
      });
    }

    _resumeScrolling () {
      this.parentNode.querySelectorAll('scrolling-image').forEach((item) => {
        item.style.animationPlayState = 'running';
      });
    }

    _duplicateIfNeeded () {
      const container = this;
      const images = container.querySelectorAll('yg-image');
      let totalWidth = 0;

      images.forEach((img) => {
        totalWidth += img.clientWidth;
      });

      if (totalWidth < container.clientWidth) {
        const cloneNeeded = images.length < 6;
        if (cloneNeeded) {
          images.forEach((img) => {
            const clone = img.cloneNode(true);
            container.appendChild(clone);
          });
        }
      }
    }
  }
  if (!window.customElements.get('scrolling-image')) {
    window.customElements.define('scrolling-image', ScrollingImages);
  }
  class YGSwiper extends HTMLElement {
    constructor() {
      super()
    }
    connectedCallback () {
      this.initSwiper()
    }
    initSwiper () {
      const swiper = new Swiper(this, {
        slidesPerView: 3,
        spaceBetween: 12,

        breakpoints: {
          320: {  //当屏幕宽度大于等于320
            slidesPerView: 1.2,
            spaceBetween: 16
          },
          768: {  //当屏幕宽度大于等于768 
            slidesPerView: 2,
            spaceBetween: 12
          },
          1280: {  //当屏幕宽度大于等于1280
            slidesPerView: 3,
            spaceBetween: 12
          }
        },
        pagination: {
          el: '.yg-swiper-pagination',
        },
        navigation: {
          nextEl: '.yg-swiper-button-next',
          prevEl: '.yg-swiper-button-prev',
        }
      }
      )
    }
  }
  if (!window.customElements.get('yg-swiper')) {
    window.customElements.define('yg-swiper', YGSwiper)
  }
  class YGSwiper_4 extends HTMLElement {
    constructor() {
      super()
    }
    connectedCallback () {
      this.columns = this.getAttribute('columns') || 4
      this.columns_mobile = this.getAttribute('columns_mobile') || 1.2

      this.initSwiper()
    }
    initSwiper () {
      const swiper = new Swiper(this, {
        slidesPerView: this.columns,
        spaceBetween: 12,

        breakpoints: {
          320: {  //当屏幕宽度大于等于320
            slidesPerView: this.columns_mobile,
            spaceBetween: 16
          },
          768: {  //当屏幕宽度大于等于768 
            slidesPerView: 3,
            spaceBetween: 12
          },
          1280: {  //当屏幕宽度大于等于1280
            slidesPerView: this.columns,
            spaceBetween: 12
          }
        },
        
        navigation: {
          nextEl: '.yg-swiper-button-next',
          prevEl: '.yg-swiper-button-prev',
        },
         scrollbar: {
          el: '.yg-swiper-scrollbar',
        },
      }
      )
    }
  }
  if (!window.customElements.get('yg-swiper-4')) {
    window.customElements.define('yg-swiper-4', YGSwiper_4)
  }
  class YGSwiperVideo extends HTMLElement {
    constructor() {
      super()
    }
    connectedCallback () {
      this.initSwiper()
    }
    initSwiper () {
      const swiper = new Swiper(this, {
        slidesPerView: 3,
        spaceBetween: 32,

        breakpoints: {
          320: {  
            slidesPerView: 1.2,
            spaceBetween: 12
          },
          768: {  
            slidesPerView: 2,
            spaceBetween: 12
          },
          1280: { 
            slidesPerView: 3,
            spaceBetween: 12
          }
        },
        navigation: {
          nextEl: '.yg-swiper-button-next',
          prevEl: '.yg-swiper-button-prev',
        },
         scrollbar: {
          el: '.yg-swiper-scrollbar',
        },
      }
      )
    }
  }
  if (!window.customElements.get('yg-swiper-video')) {
    window.customElements.define('yg-swiper-video', YGSwiperVideo)
  }
  class YGSwiperCard extends HTMLElement {
    constructor() {
      super()
    }
    connectedCallback () {
      this.initSwiper()
    }
    initSwiper () {
      const swiper = new Swiper(this, {
        slidesPerView: 2,
        spaceBetween: 32,

        breakpoints: {
          320: {  
            slidesPerView: 1,
            spaceBetween: 12
          },
          768: {  
            slidesPerView: 2,
            spaceBetween: 12
          },
          1280: { 
            slidesPerView: 2,
            spaceBetween: 12
          }
        },
        navigation: {
          nextEl: '.yg-swiper-button-next',
          prevEl: '.yg-swiper-button-prev',
        },
         pagination: {
          el: '.yg-swiper-pagination',
        },
      }
      )
    }
  }
  if (!window.customElements.get('yg-swiper-card')) {
    window.customElements.define('yg-swiper-card', YGSwiperCard)
  }
   class YGBuyItNow extends HTMLElement {
  constructor() {
    super()
    this.init()
  }
  init() {
    // 这里放置初始化逻辑
    let _this = this
    let $button = _this.querySelector('.yg-buy-it-now-button')
    $button.addEventListener('click', () => _this.buttonClick())
  }
  buttonClick() {
    // 这里放置自定义逻辑
    let buyBtn = this.querySelector('.shopify-payment-button .shopify-payment-button__more-options') || this.querySelector('shopify-buy-it-now-button .shopify-payment-button__button');
    buyBtn.click();
  }
}
if (!window.customElements.get('yg-buy-it-now')) {
  window.customElements.define('yg-buy-it-now', YGBuyItNow)
}
class YGPopupBox extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (e) => {
      e.stopPropagation

        this.closePopup(e, () => { });
      });
    }

    openPopup ($popupTemplate, fn) {
      // popupTemplate：需要替换的元素
      this.querySelector('.yg-popup-box-content-inner').innerHTML = $popupTemplate.innerHTML;
      document.querySelector('body').style.overflow = 'hidden';
      this.style.display = 'flex';
      fn();
    }
    closePopup (e, fn) {
      if (e.target.className == 'yg-popup-box-close' || e.target.className == 'yg-popup-box') {
        this.style.display = 'none';
        this.querySelector('.yg-popup-box-content-inner').innerHTML = '';
        document.querySelector('body').style.overflow = 'auto';
        fn();
      }
    }
  }
  if (!window.customElements.get('yg-popup-box')) {
    window.customElements.define('yg-popup-box', YGPopupBox);
  }
  class YGOpenPopup extends HTMLElement {
    constructor() {
      super();
      this.init();
      this.$popupBox = document.querySelector('yg-popup-box.yg-popup-box');
    }
    init () {
      // 这里放置初始化逻辑
      this.addEventListener('click', this.itemClick.bind(this));
    }
    itemClick (e) {
      e.stopPropagation

      // 这里放置自定义逻辑
      let $popupTemplate = this.querySelector('template') || this.querySelector('.yg-popup-template');

      $popupTemplate && this.$popupBox.openPopup($popupTemplate, () => { });
      
    }
  }
  if (!window.customElements.get('yg-open-popup')) {
    window.customElements.define('yg-open-popup', YGOpenPopup);
  }
});
